import debounce from 'lodash/debounce';
import uniq from 'lodash/uniq';

import { DAY_CODES } from '@/modules/public/room/services.js';
import { trackChatGptRfpSent } from '@/services/chatgpt-pixel.js';
import { getCachedActivity, setCachedActivity } from '@/utils/managerLastActivityCache.js';
import { getOnlineLabelStatus } from '@/utils/responseTime';

import { getSearchFilters } from '../../../../../../../modules/search-filters-storage';

let enquiryActivityTimer = null;
let enquiryVisibilityListenerAdded = false;

const getAuthMethod = (state) => {
  if (state.socialAuthMethod) {
    return state.socialAuthMethod;
  }
  if (state.user.isSuccesfullySignUp) {
    return 'sign_up';
  }
  if (state.user.isLoggedIn || state.user.isSuccesfullyLoggedIn) {
    return 'logged_in';
  }
  return 'sign_up';
};

const actions = {
  async init({ dispatch, commit }) {
    App.es6.shared.tags_storage().getTags()
      .then(res => commit('setEventTypes', res));

    await Promise.all([
      dispatch('fetchRoom'),
      dispatch('fetchUser'),
      dispatch('wishlist/fetchRoomInWishlist'),
    ]);
    dispatch('fetchLastFormValues');
    dispatch('fetchFormValuesLayoutAndExtendedCateringFlow');
    commit('setEnquiryFormReady', true);

    setTimeout(() => {
      $.publish('enquiry_form_loaded');
    }); // force render first before publishing

    setTimeout(() => {
      // wait for AB experiments to be completed
      dispatch('trackGA4PageView');
    }, 1000);
  },

  fetchRoom({ commit, state, dispatch }) {
    return new Promise((resolve) => {
      const { page, position, isFeatured } = App.es6.shared.sessionFlash.getFlash(`flash-room-${state.room.id}`) || {};

      $.ajax({
        method: 'get',
        dataType: 'json',
        url: state.api.roomInfo,
        data: {
          room_id: state.room.id,
          room_tag_slug: state.user.searchedEventType,
          referrer: state.referrer,
          page,
          position,
          isFeatured,
        },
      })
        .done(({ initData }) => {
          commit('setRoomAvailability', {
            partiallyBookedDays: initData.partially_booked_dates_map,
            fullyBookedDays: uniq([...initData.fully_booked_dates, ...initData.blocked_holidays]),
            openingHours: initData.opening_hours,
          });
          commit('setRoomCatering', {
            availableByVenue: initData.venue_provides_catering,
            availableExternal: initData.venue_allows_own_catering,
          });
          commit('setLastEnquiry', {
            date: initData.last_rfp_sent_at,
            url: initData.last_rfp_url,
          });
          commit('setVenueResponse', {
            rate: initData.company_response_rate,
            time: initData.company_response_time,
          });
          commit('setDateAndPeople', {
            people: initData.people || '',
            date: initData.enquiry_date,
            timeFrom: initData.enquiry_time_from,
            timeTo: initData.enquiry_time_to,
          });

          commit('setRoomSetupAllowsOP', initData.room_setup_allows_online_payments);
          commit('setLeadHubOffer', initData.lh_offer);
          commit('setNoticeHours', initData.advance_notice_hours);

          dispatch('getRoomPricing');
          dispatch('getMinimumPricing');
          resolve();
        });
    });
  },

  fetchFormValuesLayoutAndExtendedCateringFlow({ state, commit, getters }) {
    if (!getters.isLayoutAndExtendedCateringFlow) {
      return;
    }

    const json = getSearchFilters();
    if (!json) {
      return;
    }

    if (json.room_layout) {
      if (json.room_layout === 7) { // 7 - standing
        commit('setLayout', 'standing');
      } else {
        commit('setLayout', 'seating'); // 1 - sitting, 2 - boardroom, 3 - cabaret, 4 - classroom, 5 - theatre, 6 - U-shaped
      }
    }

    if (json.catering_options) {
      const offersCatering = json.catering_options.includes('1'); // 1 -> "Venue offers catering",
      const externalCateringAllowed = json.catering_options.includes('16'); // 16 -> "External catering allowed"
      const noBuyoutFee = json.catering_options.includes('1001'); // 1001 -> "No buyout fee for external catering"

      if (offersCatering && (externalCateringAllowed || noBuyoutFee)) {
        // IF "venue offers catering" AND ("External catering allowed" OR "No buyout fee for external catering") THEN
        // then nothing
      } else if (offersCatering) {
        // ELSE IF "venue offers catering" THEN
        // SET enquiry_form.catering_preference = "I need venue to provide catering or drinks"
        commit('setCatering');
      } else if (externalCateringAllowed || noBuyoutFee) {
        // ELSE IF "External catering allowed" OR "No buyout fee for external catering" THEN
        // SET enquiry_form.catering_preference = "I want my own catering"
        commit('setOwnCatering');
      }
    }

    // layout still empty
    if (!state.form.dateAndPeople?.layout) {
      const eventType = state.user.searchedEventType;

      // For drinks reception, nightclub hire and live music event types - “Standing layout” should be preselected
      const standingEventTypes = [
        'drinks-reception',
        'nightclub-hire',
        'live-music',
      ];

      // For private dining, baby shower and restaurant event types - “Seating layout” should be preselected
      const seatingEventTypes = [
        'private-dining-room',
        'baby-shower',
        'restaurant',
        'private-dining',
      ];

      if (standingEventTypes.includes(eventType)) {
        commit('setLayout', 'standing');
      } else if (seatingEventTypes.includes(eventType)) {
        commit('setLayout', 'seating');
      } else {
        // For all other event types - “Flexible layout” should be preselected
        commit('setLayout', 'flexible');
      }
    }
  },

  fetchLastRfpData({ commit, state }) {
    return new Promise((resolve) => {
      $.ajax({
        method: 'POST',
        dataType: 'json',
        url: state.api.fetchLastRfpDataUrl,
        data: {
          room_id: state.room.id,
          _token: state.api.token,
        },
      })
        .done((data) => {
          commit('setLastEnquiry', {
            date: data.last_rfp_sent_at,
            url: data.last_rfp_url,
          });
          resolve();
        });
    });
  },

  // The layout already requests auth status for the navbar (user-data.twig) and publishes it as
  // TGV_USER_DATA, so the form reuses that answer instead of asking a second time. `fresh` forces
  // a new request, e.g. after the login state changed.
  fetchUser({ state, commit }, { fresh = false } = {}) {
    const applyUser = (data) => {
      if (data?.type === 'user') {
        commit('setUserLoggedIn');
      }
    };
    const fetchFresh = () => new Promise((resolve) => {
      $.ajax({
        method: 'get',
        dataType: 'json',
        url: state.api.auth,
      })
        .done(applyUser)
        .always(resolve);
    });

    if (fresh || typeof window.__TGV_USER_DATA_LOAD !== 'function') {
      return fetchFresh();
    }

    if (window.__TGV_USER_DATA !== undefined) {
      applyUser(window.__TGV_USER_DATA);
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let settled = false;
      const settle = (data) => {
        if (settled) {
          return;
        }
        settled = true;
        applyUser(data);
        resolve();
      };
      $.subscribe('TGV_USER_DATA', settle);
      // the navbar request failed or is slow: don't hold the form hostage, ask ourselves
      window.setTimeout(() => {
        if (!settled) {
          fetchFresh().then(() => settle(window.__TGV_USER_DATA));
        }
      }, 3000);
    });
  },

  fetchLastFormValues({ commit }) {
    const lastForm = App.es6.views.quick_estimate.form_data_keeper().get();
    if (!lastForm || Object.keys(lastForm).length === 0) {
      return;
    }
    commit('setLastForm', lastForm);
  },

  openPricingForDate({ state }) {
    if (!state.form.dateAndPeople.date) {
      return;
    }

    let [day, month, year] = state.form.dateAndPeople.date.split('/');
    // parse to number and count month from 0 as JS does
    [day, month, year] = [+day, +month - 1, +year];

    const dayOfWeek = (new Date(year, month, day)).getDay();
    const dayCode = DAY_CODES[dayOfWeek];

    if (window.__PricingTableComponent) {
      window.__PricingTableComponent.showDate(dayCode, { initial: true });
    } else {
      window.__TGV_ROOM_DEFAULT_DATE = dayCode;
    }
  },

  getRoomPricing: debounce(({ dispatch }) => {
    dispatch('getRoomPricingAjax');
  }, 100),

  getRoomPricingAjax({ state, commit }) {
    if (!state.roomSetupAllowsOP) {
      return;
    }

    const { date, timeFrom, timeTo } = state.form.dateAndPeople;

    if (!date || !timeFrom || !timeTo || timeFrom === timeTo) {
      commit('setPricingLoading', false);
      commit('setPaymentDetails', null);
      return;
    }

    commit('setPricingLoading', true);
    $.ajax({
      method: 'get',
      dataType: 'json',
      url: state.api.roomPricingRoute,
      data: {
        eventDate: date,
        timeFrom,
        timeTo,
      },
    })
      .done((data) => {
        commit('setPaymentDetails', data.paymentDetails);
        commit('setPricingLoading', false);
      });
  },
  getMinimumPricing: debounce(({ dispatch }) => {
    dispatch('getMinimumPricingAjax');
  }, 100),

  getMinimumPricingAjax({ state, commit }) {
    if (!state.roomSetupAllowsOP) {
      return;
    }

    let { date, timeFrom, timeTo } = state.form.dateAndPeople;
    if (!date) {
      date = null;
      timeFrom = null;
      timeTo = null;
    }

    $.ajax({
      method: 'get',
      dataType: 'json',
      url: state.api.lowestPriceRoute,
      data: {
        eventDate: date || '',
        timeFrom: timeFrom || '',
        timeTo: timeTo || '',
      },
    })
      .done((data) => {
        commit('setMinimumPricing', data);
      });
  },
  trackGA4PageView({ getters }) {
    const params = { ...getters.ga4ParamsBase() };
    App.track.pageViewEvent(params);
    App.track.event('room_page_pageview', params);
  },
  trackGA4MessageHostInteraction({ getters }, { element, value = null, section, interaction = 'click' }) {
    const data = {
      ...getters.ga4ParamsBase(),
      element,
      section,
      interaction,
    };

    if (value !== null) {
      data.element_value = value;
    }

    App.track.event('room_page_message_host_interaction', data);
  },
  trackFindSimilarVenues({ getters }) {
    const data = {
      ...getters.ga4ParamsBase(),
      section: 'room_page_warning',
      element: 'cta_button',
      element_value: 'find_similar_venues',
      interaction: 'click',
    };

    App.track.event('clicked_to_search_page', data);
  },
  trackGA4Interaction({ getters }, { element, value, section, interaction }) {
    const data = {
      ...getters.ga4ParamsBase(),
      element,
      section,
      interaction,
    };

    if (value !== null) {
      data.element_value = value;
    }

    App.track.event('room_page_enquiry_form_interaction', data);
  },

  trackGA4NewEnquiry({ state, getters }, { userId, rfpId, enquiryId, estimatedValue }) {
    const data = {
      ...getters.ga4ParamsBase(),
      auth_method: getAuthMethod(state),
      tagvenue_user_id: userId, // to be removed in the future
      user_id: App.track.getUserIdProperty(null, userId),
      rfp_id: rfpId,
      enquiry_id: enquiryId,
      context: getters.roomPagesContextForTracking,
      value: estimatedValue,
      currency: 'GBP', // always use same GDP, despite original currency - simplified for Adwords
    };

    App.track.event('new_enquiry_created', data);
  },
  trackGA4Enquiry({ state, getters }, { userId, rfpId, enquiryId, estimatedValue }) {
    const data = {
      ...getters.ga4ParamsBase(),
      auth_method: getAuthMethod(state),
      tagvenue_user_id: userId, // to be removed in the future
      user_id: App.track.getUserIdProperty(null, userId),
      rfp_id: rfpId,
      enquiry_id: enquiryId,
      context: getters.roomPagesContextForTracking,
      exp_response_time: state.responseTime ? Math.round(state.responseTime / 60) : null,
      exp_response_rate: state.responseRate ?? null,
      exp_online_status: getOnlineLabelStatus(state?.managerLastActivityOnline),
      value: estimatedValue,
      currency: 'GBP', // always use same GDP, despite original currency - simplified for Adwords
    };
    App.track.event('room_page_enquiry_sent', data);
    App.track.event('rfp_sent', data);
    trackChatGptRfpSent(estimatedValue);
  },
  /* eslint-disable camelcase */
  trackGA4ExperimentImpression({ getters }, { experimentId, version }) {
    App.track.event('experiment_impression', {
      ...getters.ga4ParamsBase(),
      experiment_id: experimentId,
      variant_id: version,
    });
  },
  /* eslint-enable */
  getManagerLastActivityOnline({ state, commit, dispatch }) {
    if (!state.venue.id) {
      return;
    }

    // Register visibility listener once — pause polling when tab hidden
    if (!enquiryVisibilityListenerAdded) {
      enquiryVisibilityListenerAdded = true;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          clearTimeout(enquiryActivityTimer);
          enquiryActivityTimer = null;
        }
      });
    }

    // clean up previous timeout
    if (enquiryActivityTimer) {
      clearTimeout(enquiryActivityTimer);
      enquiryActivityTimer = null;
    }

    // Serve from cache if still fresh
    const cached = getCachedActivity(state.venue.id);
    if (cached !== false) {
      commit('setManagerLastActivityOnline', cached);
      enquiryActivityTimer = setTimeout(() => {
        dispatch('getManagerLastActivityOnline');
      }, 15 * 60 * 1000); // every 15 minutes
      return;
    }

    const endpoint = '/venue-data/last-manager-activity';
    $.ajax({
      url: endpoint,
      data: { ids: [state.venue.id] },
      dataType: 'json',
    })
      .done((data) => {
        const value = data.lastManagerActivity[state.venue.id];
        setCachedActivity(state.venue.id, value);
        commit('setManagerLastActivityOnline', value);
        enquiryActivityTimer = setTimeout(() => {
          dispatch('getManagerLastActivityOnline');
        }, 15 * 60 * 1000); // every 15 minutes
      });
  },
};

export default actions;
